import React, { useState, useMemo, useEffect, useLayoutEffect } from "react";
import { Users, Plus, RefreshCw, AlertTriangle } from "lucide-react";
import DomainHero from "../components/shared/DomainHero";
import { useDistrictGuard } from "../hooks/useDistrictGuard";
import { useFamiliesRanking } from "@/features/families/hooks";
import { usePeople } from "@/features/people/hooks";
import { usePullToRefresh } from "../hooks/usePullToRefresh";
import TopBar from "../components/TopBar";
import SearchInput from "../components/SearchInput";
import EmptyState from "../components/shared/EmptyState";
import { SkeletonListItem } from "../components/shared/Skeletons";
import FamilyListCard from "@/features/families/components/FamilyListCard";
import PersonListCard from "@/features/families/components/PersonListCard";
import { normalizeText } from "../lib/search-rank";
import { InlineIconButton } from "../components/Forms";
import { useCoordinatorChurchName } from "../hooks/useCoordinatorChurchName";

export default function Families() {
  const { district, coordinatorChurchId } = useDistrictGuard();
  const coordinatorChurchName = useCoordinatorChurchName();
  const { families, loading, error, refetch } = useFamiliesRanking();
  const { people } = usePeople(
    coordinatorChurchId
      ? { church_id: coordinatorChurchId }
      : district?.id
      ? { district_id: district.id }
      : {}
  );
  const [search, setSearch] = useState(() => {
    try { return sessionStorage.getItem("keves:families:search") || ""; } catch { return ""; }
  });
  const handleSearch = (v) => {
    setSearch(v);
    if (v) sessionStorage.setItem("keves:families:search", v);
    else sessionStorage.removeItem("keves:families:search");
  };
  const { containerRef, pullProgress, refreshing } = usePullToRefresh(refetch);

  // Restore scroll position before first paint — prevents jumping to top on back-navigation
  useLayoutEffect(() => {
    try {
      const saved = sessionStorage.getItem("keves:families:scrollY");
      if (saved) {
        window.scrollTo(0, parseInt(saved, 10));
        sessionStorage.removeItem("keves:families:scrollY");
      }
    } catch { /* ignore — private mode or storage unavailable */ }
  }, []);

  // Save scroll position when navigating away so it can be restored above
  useEffect(() => {
    return () => {
      try { sessionStorage.setItem("keves:families:scrollY", String(Math.round(window.scrollY))); } catch { /* ignore */ }
    };
  }, []);

  const filteredFamilies = useMemo(() => {
    if (!search.trim()) return families;
    const q = normalizeText(search);
    return families.filter((f) => normalizeText(f.name).includes(q));
  }, [families, search]);

  const filteredPeople = useMemo(() => {
    if (search.trim().length < 2) return [];
    const q = normalizeText(search);
    return (people || []).filter((p) => normalizeText(p.name || "").includes(q)).slice(0, 30);
  }, [people, search]);

  const isSearching = search.trim().length > 0;

  const PAGE_SIZE = 30;
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
  useEffect(() => { setVisibleCount(PAGE_SIZE); }, [search]);

  const visibleFamilies = isSearching ? filteredFamilies : filteredFamilies.slice(0, visibleCount);
  const hasMoreFamilies = !isSearching && filteredFamilies.length > visibleCount;

  return (
    <div ref={containerRef} className="anim-in min-h-[100dvh] bg-page-bg pb-[calc(96px+env(safe-area-inset-bottom))]">

      {/* PULL INDICATOR */}
      {(pullProgress > 0 || refreshing) && (
        <div
          className="flex items-center justify-center overflow-hidden"
          style={{
            height: `${Math.min(pullProgress, 1) * 44}px`,
            opacity: Math.min(pullProgress * 1.4, 1),
            transition: refreshing ? "none" : "height 150ms ease-out, opacity 150ms ease-out",
          }}
        >
          <RefreshCw
            size={16}
            className={`text-slate-400 dark:text-slate-500 ${refreshing ? "animate-spin" : ""}`}
            style={{
              transform: `rotate(${Math.min(pullProgress, 1) * 220}deg) scale(${0.7 + Math.min(pullProgress, 1) * 0.3})`,
              transition: refreshing ? "none" : "transform 100ms ease-out",
            }}
          />
        </div>
      )}

      <TopBar
        title="Hogares y personas"
        subtitle={coordinatorChurchId ? (coordinatorChurchName || undefined) : district?.name}
        safeFallback="/district"
        right={
          <InlineIconButton icon={Plus} to="/families/new" accent="navy" aria-label="Nueva familia" />
        }
      />

      {/* INLINE SEARCH */}
      <div className="px-4 pt-2">
        <SearchInput
          value={search}
          onChange={handleSearch}
          placeholder="Buscar hogar o persona..."
        />
      </div>

      {/* HERO — always rendered while loading or when families exist to prevent layout shift */}
      {(loading || families.length > 0) && (
        <DomainHero
          domain="pastoral"
          title="Hogares y personas"
          subtitle={district?.name || "Distrito"}
          icon={<Users size={20} />}
          variant="compact"
          titleClassName="truncate"
          actions={
            <div className="text-right shrink-0 flex flex-col justify-center h-12">
              <p className="text-[22px] font-bold leading-none text-white">
                {loading ? "—" : families.length}
              </p>
              <p className="text-[10px] font-semibold uppercase tracking-widest text-white/60 mt-1">Hogares</p>
            </div>
          }
        />
      )}

      <div className="px-4 pt-3 pb-5 space-y-3">

        {/* LOADING */}
        {loading && !isSearching && (
          <div className="space-y-3 animate-in fade-in duration-300">
            {[1, 2, 3, 4].map((i) => <SkeletonListItem key={i} />)}
          </div>
        )}

        {/* ERROR */}
        {!loading && error && (
          <EmptyState
            icon={<AlertTriangle size={28} />}
            title="No se pudo cargar"
            description="Verifica tu conexión e intenta de nuevo."
            actionLabel="Reintentar"
            actionOnClick={refetch}
          />
        )}

        {/* EMPTY — sin hogares */}
        {!loading && !error && families.length === 0 && (
          <EmptyState
            icon={<Users size={28} />}
            title="Sin hogares registrados"
            description="Los hogares son la base del acompañamiento pastoral en el distrito."
            actionLabel="Registrar primer hogar"
            actionHref="/families/new"
          />
        )}

        {/* EMPTY — sin resultados de búsqueda */}
        {!loading && isSearching && filteredFamilies.length === 0 && filteredPeople.length === 0 && (
          <EmptyState compact icon={<Users size={20} />} title={`Sin resultados para "${search}"`} />
        )}

        {/* PERSONAS (solo en búsqueda) */}
        {filteredPeople.length > 0 && (
          <>
            <div className="flex items-center gap-2">
              <p className="text-[11px] font-bold uppercase tracking-wider text-slate-400 dark:text-slate-500 shrink-0">Personas</p>
              <div className="h-px flex-1 bg-slate-200/60 dark:bg-slate-700/50" />
            </div>
            {filteredPeople.map((p) => (
              <PersonListCard key={p.id} person={p} />
            ))}
            {filteredFamilies.length > 0 && (
              <div className="flex items-center gap-2 pt-1">
                <p className="text-[11px] font-bold uppercase tracking-wider text-slate-400 dark:text-slate-500 shrink-0">Hogares</p>
                <div className="h-px flex-1 bg-slate-200/60 dark:bg-slate-700/50" />
              </div>
            )}
          </>
        )}

        {/* HOGARES */}
        {visibleFamilies.map((f, idx) => (
          <FamilyListCard key={f.id} family={f} animationIndex={isSearching ? undefined : idx} />
        ))}

        {/* LOAD MORE */}
        {hasMoreFamilies && (
          <button
            onClick={() => setVisibleCount((c) => c + PAGE_SIZE)}
            className="w-full h-11 rounded-[14px] bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-[12px] font-semibold text-slate-500 dark:text-slate-400 flex items-center justify-center gap-2 transition-all duration-200 active:scale-[0.98] active:opacity-80"
          >
            Cargar más ({filteredFamilies.length - visibleCount} restantes)
          </button>
        )}

      </div>
    </div>
  );
}
